import React, { useRef, useEffect } from 'react';

const ParticleCanvas: React.FC = () => {
    const canvasRef = useRef<HTMLCanvasElement>(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        let animationFrameId: number;
        let particles: Particle[] = [];

        class Particle {
            x: number;
            y: number;
            size: number;
            speedX: number;
            speedY: number;

            constructor(x: number, y: number, size: number, speedX: number, speedY: number) {
                this.x = x;
                this.y = y;
                this.size = size;
                this.speedX = speedX;
                this.speedY = speedY;
            }

            update() {
                if (this.x > canvas.width || this.x < 0) this.speedX = -this.speedX;
                if (this.y > canvas.height || this.y < 0) this.speedY = -this.speedY;

                this.x += this.speedX;
                this.y += this.speedY;
            }

            draw() {
                if (!ctx) return;
                ctx.fillStyle = 'rgba(58, 171, 210, 0.5)';
                ctx.beginPath();
                ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
                ctx.fill();
            }
        }

        const init = () => {
            particles = [];
            // Dynamically adjust particle count based on screen width to reduce load
            let particleCount;
            if (canvas.width < 768) {
                particleCount = 240; // Mobile
            } else if (canvas.width < 1280) {
                particleCount = 360; // Tablet / Small Desktop
            } else {
                particleCount = 480; // Large Desktop
            }

            for (let i = 0; i < particleCount; i++) {
                const size = Math.random() * 2 + 1;
                const x = Math.random() * canvas.width;
                const y = Math.random() * canvas.height;
                const speedX = Math.random() * 0.6 - 0.3; // Slower speed for a calmer feel
                const speedY = Math.random() * 0.6 - 0.3;
                particles.push(new Particle(x, y, size, speedX, speedY));
            }
            
            // Simulate a few frames to make it look like it's already running
            for (let i = 0; i < 60; i++) { // Approx 1 second of updates
                particles.forEach(p => p.update());
            }
        };
        
        const resizeCanvas = () => {
            canvas.width = canvas.offsetWidth;
            canvas.height = canvas.offsetHeight;
            init();
        };

        window.addEventListener('resize', resizeCanvas);

        const connect = () => {
            if (!ctx) return;
            let opacityValue = 1;
            const connectDistance = 120;
            for (let a = 0; a < particles.length; a++) {
                for (let b = a; b < particles.length; b++) {
                    const distance = Math.sqrt(
                        Math.pow(particles[a].x - particles[b].x, 2) + Math.pow(particles[a].y - particles[b].y, 2)
                    );
                    if (distance < connectDistance) {
                        opacityValue = 1 - (distance / connectDistance);
                        ctx.strokeStyle = `rgba(58, 171, 210, ${opacityValue})`;
                        ctx.lineWidth = 0.5;
                        ctx.beginPath();
                        ctx.moveTo(particles[a].x, particles[a].y);
                        ctx.lineTo(particles[b].x, particles[b].y);
                        ctx.stroke();
                    }
                }
            }
        };

        const animate = () => {
            if (!ctx) return;
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            particles.forEach(p => {
                p.update();
                p.draw();
            });
            
            connect();

            animationFrameId = requestAnimationFrame(animate);
        };

        resizeCanvas(); // Initial setup
        animationFrameId = requestAnimationFrame(animate);

        return () => {
            cancelAnimationFrame(animationFrameId);
            window.removeEventListener('resize', resizeCanvas);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return <canvas ref={canvasRef} className="absolute top-0 left-0 w-full h-full z-10" />;
};

export default ParticleCanvas;
